import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import {
  CircleIcon,
  Button,
  Label,
  InputField as BaseInputField,
  PhoneField,
  SelectField,
  FormModal
} from '@ubeya/shared-web/components';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import { required, startsWithCapital, validateNameValue } from '@ubeya/shared/utils/form';
import useToaster from '@ubeya/shared/hooks/useToaster';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseExcelIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import privileges from '@ubeya/shared-web/privileges';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import { MAX_FIRST_LAST_NAME_LENGTH } from 'employees/constants';

const Wrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const InputField = styled(BaseInputField)`
  > div {
    flex: unset;
  }
`;

const ExcelIcon = styled(BaseExcelIcon)`
  margin-right: 10px;
`;

const Content = styled.div`
  padding: 30px 41px;
  display: grid;
  grid-template-columns: repeat(${({ $numberOfColumns }) => $numberOfColumns}, 1fr) auto;
  grid-gap: 20px;
`;

const UploadEmployeesText = styled.div`
  font-size: 16px;
  margin: 30px;
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.link};
  text-decoration: underline;
  cursor: pointer;
`;

const CloseButton = styled(CircleIcon)`
  ${({ hidden }) => hidden && 'visibility: hidden;'}
  &&& {
    width: auto;
  }
`;

const AddButton = styled(Button).attrs(() => ({ secondary: true, type: 'button' }))`
  grid-column: 1/-1;
  max-width: 240px;
  margin-top: 20px;
`;

const Employees = () => {
  const { t } = useContext(ConfigContext);
  const { branchesOptions } = useBranches();
  const { positionsOptions } = usePositions();

  const showBranches = branchesOptions.length > 1;
  const showPositions = positionsOptions.length > 1;

  const branches = branchesOptions.length === 1 ? [branchesOptions[0].value] : undefined;

  const numberOfColumns = 5 - (showBranches ? 0 : 1) - (showPositions ? 0 : 1);

  return (
    <Wrapper>
      <Content $numberOfColumns={numberOfColumns}>
        <Label title={t('firstName')} required />
        <Label title={t('lastName')} />
        <Label title={t('phone')} />
        {showBranches && <Label title={t('branches')} required />}
        {showPositions && <Label title={t('positions')} />}
        <div />

        <FieldArray name="employees">
          {({ fields }) => (
            <>
              {fields.map((name, index) => (
                <React.Fragment key={index}>
                  <InputField
                    name={`employees[${index}].firstName`}
                    onChange={startsWithCapital}
                    required
                    validate={(value, falseInclude) =>
                      validateNameValue({
                        value,
                        falseInclude,
                        isRequired: true,
                        maxLength: MAX_FIRST_LAST_NAME_LENGTH
                      })
                    }
                    metadata={{ maxNumberOfChar: MAX_FIRST_LAST_NAME_LENGTH }}
                  />

                  <InputField
                    name={`employees[${index}].lastName`}
                    onChange={startsWithCapital}
                    validate={(value, falseInclude) =>
                      validateNameValue({
                        value,
                        falseInclude,
                        isRequired: false,
                        maxLength: MAX_FIRST_LAST_NAME_LENGTH
                      })
                    }
                    metadata={{ maxNumberOfChar: MAX_FIRST_LAST_NAME_LENGTH }}
                  />

                  <PhoneField name={`employees[${index}].phone`} />

                  {showBranches && (
                    <SelectField name={`employees[${index}].branches`} options={branchesOptions} validate={required} />
                  )}

                  {showPositions && <SelectField name={`employees[${index}].positions`} options={positionsOptions} />}

                  <CloseButton
                    hidden={fields.value.length === 1}
                    icon={CloseIcon}
                    onClick={() => fields.remove(index)}
                  />
                </React.Fragment>
              ))}

              <AddButton onClick={() => fields.push({ id: -Date.now(), branches })}>
                + {t('addMoreEmployees')}
              </AddButton>
            </>
          )}
        </FieldArray>
      </Content>
    </Wrapper>
  );
};

const AddEmployees = ({ onClose, onAddEmployees, handleImportEmployees }) => {
  const { t } = useContext(ConfigContext);
  const { addBulkEmployees } = useEmployee();
  const { branchesOptions, branches: allBranches } = useBranches();
  const { addError, addSuccess } = useToaster();

  const initialValues = useMemo(() => {
    const branches = branchesOptions.length === 1 ? [branchesOptions[0].value] : undefined;

    return {
      employees: Array(3)
        .fill()
        .map(() => ({ branches }))
    };
  }, [branchesOptions]);

  const handleSubmit = useCallback(
    async (values) => {
      const employees = values.employees
        .filter(({ firstName, branches }) => firstName && branches)
        .map(({ firstName, lastName, phone, branches, positions }) => ({
          firstName,
          lastName,
          phone,
          branches,
          positions,
          ...(allBranches.find(({ id }) => branches.includes(id))?.employeeConfig || {})
        }));

      const { success, failed } = await addBulkEmployees({ employees });

      if (success?.length === 1) {
        addSuccess(t('successAddEmployee'));
      }

      if (success?.length > 1) {
        addSuccess(t('addEmployeesSuccess', { count: success.length }));
      }

      (failed || []).forEach(({ phone }) =>
        addError(
          t('addEmployeeFailed', { phone: phone ? parsePhoneNumberFromString(`+${phone}`)?.formatNational?.() : phone })
        )
      );

      onClose();
      onAddEmployees?.();
    },
    [addBulkEmployees, onClose, onAddEmployees, allBranches, addSuccess, t, addError]
  );

  const handleUploadEmployeesFromExcel = () => {
    onClose();
    handleImportEmployees?.();
  };

  return (
    <FormModal
      size="xlarge"
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      onSubmit={handleSubmit}
      title={t('addEmployees')}
      onClose={onClose}
      confirmText="add"
      showCancelButton
      isPaddingContent={false}
      disabledOnPristine={false}>
      <Employees />
      {!!handleImportEmployees && (
        <UploadEmployeesText onClick={handleUploadEmployeesFromExcel}>
          <ExcelIcon />
          {t('addEmployeesImportFromExcel')}
        </UploadEmployeesText>
      )}
    </FormModal>
  );
};

export default withPermissions(AddEmployees, {
  action: { privileges: [privileges.ADD_EMPLOYEE] },
  read: { privileges: [privileges.ADD_EMPLOYEE] }
});
